import { AxiosV2 } from "@/services/axios";
import { processStream } from "../utils/streamUtils";

/**
 * Start a new chat conversation
 * @param {string} userId - The user ID
 * @param {string} input - The initial message
 * @returns {Promise} - Response with conversationId
 */
export const startChat = async (userId, input) => {
  const { data } = await AxiosV2.post("/chat/start", {
    userId,
    input,
  });
  return data;
};

/**
 * Get all conversations for a user
 * @param {string} userId - The user ID
 * @returns {Promise} - Array of conversations
 */
export const getConversations = async (userId) => {
  const { data } = await AxiosV2.get(`/chat/user/${userId}`);
  return data;
};

/**
 * Get a specific conversation by ID
 * @param {string} conversationId - The conversation ID
 * @returns {Promise} - Conversation details with messages
 */
export const getConversationById = async (conversationId) => {
  const { data } = await AxiosV2.get(`/chat/conversation/${conversationId}`);
  return data;
};

/**
 * Delete a conversation
 * @param {string} conversationId - The conversation ID
 * @returns {Promise} - Deletion confirmation
 */
export const deleteConversation = async (conversationId) => {
  const { data } = await AxiosV2.delete(`/chat/${conversationId}`);
  return data;
};

/**
 * Send a message with streaming response (SSE)
 * @param {string} conversationId - The conversation ID
 * @param {string} userId - The user ID
 * @param {string} input - The message content
 * @param {Function} onChunk - Callback for each chunk of data
 * @param {Function} onComplete - Callback when streaming is complete
 * @param {Function} onError - Callback for errors
 * @returns {Promise<Function>} - Cleanup function to abort the stream
 */
export const sendMessageStream = async (
  conversationId,
  userId,
  input,
  onChunk,
  onComplete,
  onError
) => {
  const baseURL = AxiosV2.defaults.baseURL || "";

  try {
    // Get fresh token from Clerk
    const token = await window.Clerk?.session?.getToken();

    const response = await fetch(`${baseURL}/chat/${conversationId}/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({ userId, input }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    processStream(reader, decoder, onChunk, onComplete, onError);

    return () => reader.cancel();
  } catch (error) {
    console.error("Streaming error:", error);
    onError?.(error);
    throw error;
  }
};
